import { ApiError } from "@aidkitorg/aidkit/lib/common/api.types";
import { SummaryExpr } from "@aidkitorg/types/lib/survey";
import * as Sentry from "@sentry/react";

// TODO: i18n
export const generateTimestampString =  (timestamp: number) => {
  const date = new Date(timestamp);

  return (
    <>
      {date.toLocaleDateString()} {date.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "numeric",
      })}
    </>
  )
}

/**
 * Given a timestamp returned by something like Date.now(), returns that time
 * formatted in MM/DD/YYYY format.
 * 
 * @param timestamp timestamp in ms elapsed since epoch
 */
export const generateDateString = (timestamp: number) => {
  const date = new Date(timestamp);

  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();

  const formattedDate = `${month}/${day}/${year}`;

  return formattedDate
}

/**
 * Given a candidate string, validates whether it is a valid email format. 
 * Used in `InputWithTags` component.
 * 
 * @param candidate candidate to validate
 * @returns boolean 
 */
export const onlyEmails = (candidate: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(candidate)
};

/**
 * Given an error, logs the error as an admin panel error in Sentry
 * 
 * @param error error to log
 */
export const logAdminPanelError = (error: ApiError, message: string) => {
  Sentry.withScope((scope) => {
    scope.setTag("surface_area", "admin_panel")
    scope.setTag("error_code", error.code)
    scope.setTag("error_source", error.source)
    
    Sentry.captureException(new Error('Admin Panel Error ' + error.code + ": " + message))
  })
}  

/**
 * Given an aggregation type and a data field, genearates a distro Summary kind
 * 
 * @param aggregation aggregation type for summary
 * @param dataField target field to aggregate on
 */
export const generateDistroSummaryKind = (aggregation: string, dataField: string): SummaryExpr => {
  switch (aggregation) {
    case 'Count': 
      return { kind: 'Count' }
    case 'Sum':
      return {
        kind: 'Sum',
        value: {
          kind: 'Field',
          field: dataField
        }
      }
    case 'Mean':
      return {
        kind: 'Mean',
        value: {
          kind: 'Field', 
          field: dataField
        }
      }
  }

  return { kind: 'Count' }
}

/**
 * Converts a string into snake_case format.
 *
 * This function:
 * - Converts camelCase to snake_case
 * - Replaces spaces and dashes with underscores
 * - Removes non-alphanumeric characters (excluding underscores)
 * - Lowercases the entire result
 *
 * @param input - The input string to convert (e.g. a dashboard title)
 * @returns The snake_case version of the input string
 *
 * @example
 * toSnakeCase("Dashboard Title") // "dashboard_title"
 * toSnakeCase("RevenueByRegion2025") // "revenue_by_region2025"
 * toSnakeCase("My-Dashboard!") // "my_dashboard"
 */
export const toSnakeCase = (input: string): string => {
  return input
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1_$2')      // camelCase → camel_Case
    .replace(/[\s\-]+/g, '_')                 // spaces and dashes → underscores
    .replace(/[^a-zA-Z0-9_]/g, '')            // remove other special characters
    .toLowerCase();
}

/**
 * Returns today's date formatted as a compact YYYYMMDD string.
 *
 * This is useful for filenames, timestamps, and compact date tracking.
 * Pads month and day with leading zeros to ensure 2-digit format.
 *
 * @returns A string representing today’s date in YYYYMMDD format (e.g., "20250724")
 *
 * @example
 * getTodayAsYYYYMMDD(); // "20250724" if today is July 24, 2025
 */
export const getTodayAsYYYYMMDD = (): string => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
  const dd = String(today.getDate()).padStart(2, '0');
  return `${yyyy}${mm}${dd}`;
}

/**
 * Converts a string to PascalCase format.
 *
 * This function:
 * - Lowercases the input
 * - Splits on spaces, dashes, or underscores
 * - Capitalizes the first letter of each word
 * - Joins the words without separators
 *
 * @param input - The input string to convert (e.g., "my-dashboard name")
 * @returns The PascalCase version of the string (e.g., "MyDashboardName")
 *
 * @example
 * toPascalCase("dashboard name") // "DashboardName"
 * toPascalCase("my-dashboard")   // "MyDashboard"
 * toPascalCase("snake_case_name") // "SnakeCaseName"
 */
export const toPascalCase = (input: string): string => {
  return input
    .toLowerCase()
    .split(/[\s-_]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
}